// @ts-nocheck
/**
 * RuntimeCompiler.ts — WP-012 Template V2 Runtime Compiler
 *
 * Transforms a LoadedTemplate (already validated) into a fully indexed
 * CompiledRuntimeTemplate for O(1) lookups by downstream resolvers and renderers.
 *
 * Never throws — all errors are returned as CompileResult issues.
 * All outputs are new objects; the `loaded` input is never mutated.
 */

import type { ComponentNode, LayoutBox } from '../../types/canonical';
import type { PressHandlerInput } from '../../types/eventHandler';
import { normalizeEventHandler } from '../../types/eventHandler';
import type { LoadedTemplate } from '../loader';
import type {
    CompileResult,
    CompiledCategoryDefinition,
    CompiledExpressionDefinition,
    CompiledRuntimeAction,
    CompiledRuntimeNode,
    CompiledRuntimeTemplate,
    CompiledScriptDefinition,
    RuntimeIndexes,
} from './types';

/** Stable compile-time key: component ids are only unique per element+layout. */
export function compileNodeKey(elementId: string, layoutId: string, nodeId: string): string {
  return `${elementId}::${layoutId}::${nodeId}`;
}

/** Keys stripped from the component node when building the props record. */
const BASE_NODE_KEYS = new Set([
  'id',
  'type',
  'layout',
  'binding',
  'visibility',
  'enabled',
  'interactions',
  'semantics',
  'style',
]);

/**
 * Extracts renderer-specific props from a component node by omitting
 * all base keys defined on ComponentBase.
 */
function extractProps(node: ComponentNode): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(node as unknown as Record<string, unknown>)) {
    if (!BASE_NODE_KEYS.has(key)) {
      result[key] = (node as unknown as Record<string, unknown>)[key];
    }
  }
  return result;
}

function pushActionIdFromHandler(ids: string[], handler: PressHandlerInput | undefined): void {
  const normalized = normalizeEventHandler(handler);
  if (normalized?.kind === 'action' && !ids.includes(normalized.actionId)) {
    ids.push(normalized.actionId);
  }
}

/**
 * Collects unique action IDs declared on a component's interaction bindings.
 */
function collectActionIds(node: ComponentNode): string[] {
  const ids: string[] = [];
  pushActionIdFromHandler(ids, node.interactions?.onPress);
  pushActionIdFromHandler(ids, node.interactions?.onLongPress);
  pushActionIdFromHandler(ids, node.interactions?.onChange);
  for (const handler of node.interactions?.onChangeHandlers ?? []) {
    pushActionIdFromHandler(ids, handler);
  }
  for (const handler of node.interactions?.onPressHandlers ?? []) {
    pushActionIdFromHandler(ids, handler);
  }
  pushActionIdFromHandler(ids, node.interactions?.onLoad);
  // onDoubleTap is not yet in the canonical type but may appear in future payloads
  const onDoubleTap = (node.interactions as Record<string, unknown> | undefined)?.['onDoubleTap'] as
    | PressHandlerInput
    | undefined;
  pushActionIdFromHandler(ids, onDoubleTap);
  if (node.type === 'button') {
    pushActionIdFromHandler(ids, node.button?.action);
  }
  if (node.type === 'icon') {
    pushActionIdFromHandler(ids, node.icon?.action);
  }
  return ids;
}

/**
 * Recursively walks a component list, adding a CompiledRuntimeNode for each
 * node encountered (including nested group children).
 */
function walkComponents(
  components: ComponentNode[],
  elementId: string,
  layoutId: string,
  nodesById: Record<string, CompiledRuntimeNode>,
): void {
  for (const node of components) {
    const childIds: string[] =
      node.type === 'group'
        ? ((node as any).group?.children ?? []).map((c: ComponentNode) =>
          compileNodeKey(elementId, layoutId, c.id))
        : [];

    const visibilitySource = node.visibility?.visible;
    const visibilityExpressionId =
      visibilitySource?.kind === 'expression'
        ? visibilitySource.expressionId
        : undefined;

    const staticVisible =
      visibilitySource?.kind === 'static'
        ? visibilitySource.value
        : undefined;

    const enabledSource = node.enabled?.enabled;
    const enabledExpressionId =
      enabledSource?.kind === 'expression'
        ? enabledSource.expressionId
        : undefined;

    const staticEnabled =
      enabledSource?.kind === 'static'
        ? enabledSource.value
        : undefined;

    nodesById[compileNodeKey(elementId, layoutId, node.id)] = {
      id: node.id,
      type: node.type,
      elementId,
      layoutId,
      layout: node.layout as LayoutBox,
      props: extractProps(node),
      binding: node.binding,
      style: node.style,
      visibilitySource,
      visibilityExpressionId,
      staticVisible,
      enabledSource,
      enabledExpressionId,
      staticEnabled,
      actionIds: collectActionIds(node),
      childIds,
      interactions: node.interactions,
    };

    if (node.type === 'group') {
      const children: ComponentNode[] = (node as any).group?.children ?? [];
      walkComponents(children, elementId, layoutId, nodesById);
    }
  }
}

/**
 * Compiles a LoadedTemplate into a fully indexed CompiledRuntimeTemplate.
 * Never throws. Returns CompileResult with null compiled if a blocking error occurs.
 */
export function compileTemplate(loaded: LoadedTemplate): CompileResult {
  try {
    const nodesById: Record<string, CompiledRuntimeNode> = {};
    const actionsById: Record<string, CompiledRuntimeAction> = {};
    const scriptsById: Record<string, CompiledScriptDefinition> = {};
    const expressionsById: Record<string, CompiledExpressionDefinition> = {};
    const categoriesById: Record<string, CompiledCategoryDefinition> = {};

    // ── Step 1 — Walk element tree to build nodesById ──────────────────────
    for (const element of loaded.document.elements) {
      for (const layout of element.layouts) {
        walkComponents(layout.components, element.id, layout.id, nodesById);
      }

      // ── Step 2 — Extract actions from element.scripts.actions ──────────
      if (element.scripts?.actions) {
        for (const action of element.scripts.actions) {
          // `kind` is not on the canonical type but may be present in raw payloads;
          // fall back to `type` (the actual canonical discriminant) then to 'builtin'.
          const kind =
            (action as unknown as Record<string, unknown>)['kind'] ??
            action.type ??
            'builtin';
          actionsById[action.id] = {
            id: action.id,
            kind: String(kind),
            params: (action as unknown as Record<string, unknown>)['params'] as Record<
              string,
              unknown
            > ?? {},
            sourceNodeId: undefined,
          };
        }
      }
    }

    // Reconcile sourceNodeId: link each action back to its originating node.
    for (const compiledNode of Object.values(nodesById)) {
      for (const actionId of compiledNode.actionIds) {
        const action = actionsById[actionId];
        if (action !== undefined) {
          action.sourceNodeId = compiledNode.id;
        }
      }
    }

    // ── Step 3 — Extract scripts and expressions from document.scripts ────
    if (loaded.document.scripts) {
      for (const script of loaded.document.scripts.scripts ?? []) {
        scriptsById[script.id] = { id: script.id, body: script };
      }
      for (const expr of (loaded.document.scripts.expressions ?? [])) {
        expressionsById[expr.id] = { id: expr.id, expression: expr };
      }
    }

    // ── Step 4 — Build categoriesById ────────────────────────────────────
    for (const bundle of loaded.document.categories) {
      for (const category of bundle.categories) {
        const properties = bundle.properties
          .filter((p) => p.categoryId === category.id)
          .map((p) => ({ ...p, categoryId: category.id }));
        categoriesById[category.id] = {
          id: category.id,
          name: category.name,
          properties,
        };
      }
    }

    // ── Step 5 — Build indexes ────────────────────────────────────────────
    const childNodeIdsByParentId: Record<string, string[]> = {};
    const nodesByComponentKind: Record<string, string[]> = {};
    const actionIdsByNodeId: Record<string, string[]> = {};

    for (const node of Object.values(nodesById)) {
      if (node.childIds.length > 0) {
        childNodeIdsByParentId[node.id] = [...node.childIds];
      }
      if (nodesByComponentKind[node.type] === undefined) {
        nodesByComponentKind[node.type] = [];
      }
      nodesByComponentKind[node.type].push(node.id);
      if (node.actionIds.length > 0) {
        actionIdsByNodeId[node.id] = [...node.actionIds];
      }
    }

    const indexes: RuntimeIndexes = {
      childNodeIdsByParentId,
      nodesByComponentKind,
      actionIdsByNodeId,
    };

    // ── Step 6 — Assemble result ──────────────────────────────────────────
    const compiled: CompiledRuntimeTemplate = {
      metadata: loaded.document.metadata,
      primaryElementId: loaded.document.metadata.primaryElementId,
      nodesById,
      actionsById,
      scriptsById,
      expressionsById,
      categoriesById,
      assetManifest: loaded.assetManifest,
      indexes,
    };

    return { compiled, issues: [] };
  } catch (e) {
    return {
      compiled: null,
      issues: [
        {
          severity: 'error',
          code: 'schema.invalid_root',
          message: 'Compiler failed unexpectedly: ' + String(e),
          path: '',
        },
      ],
    };
  }
}
